"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SskyOktaService = exports.TRANSACTION_STATUS = void 0;
const okta_auth_js_1 = require("@okta/okta-auth-js");
const authService_1 = require("../authService");
const errors_1 = require("../errors");
const utils_1 = require("../utils");
var TRANSACTION_STATUS;
(function (TRANSACTION_STATUS) {
    TRANSACTION_STATUS["lockedOut"] = "LOCKED_OUT";
    TRANSACTION_STATUS["passwordExpired"] = "PASSWORD_EXPIRED";
    TRANSACTION_STATUS["passwordReset"] = "PASSWORD_RESET";
    TRANSACTION_STATUS["passwordWarn"] = "PASSWORD_WARN";
    TRANSACTION_STATUS["recovery"] = "RECOVERY";
    TRANSACTION_STATUS["recoveryChallenge"] = "RECOVERY_CHALLENGE";
    TRANSACTION_STATUS["mfaEnroll"] = "MFA_ENROLL";
    TRANSACTION_STATUS["mfaEnrollActivate"] = "MFA_ENROLL_ACTIVATE";
    TRANSACTION_STATUS["mfaRequired"] = "MFA_REQUIRED";
    TRANSACTION_STATUS["mfaChallenge"] = "MFA_CHALLENGE";
    TRANSACTION_STATUS["success"] = "SUCCESS";
})(TRANSACTION_STATUS || (exports.TRANSACTION_STATUS = TRANSACTION_STATUS = {}));
class SskyOktaService {
    constructor(config) {
        const { initiateForgotPasswordCB, confirmForgotPasswordCB, shouldRedirectToGetTokens } = config, oktaAuthOptions = __rest(config, ["initiateForgotPasswordCB", "confirmForgotPasswordCB", "shouldRedirectToGetTokens"]);
        this.oktaAuthOptions = oktaAuthOptions;
        this.authClient = new okta_auth_js_1.OktaAuth(Object.assign(Object.assign({}, oktaAuthOptions), { transformAuthState: this.transformAuthState.bind(this) }));
        // Subscribe to authState change event. Logic based on authState is done here.
        this.authClient.authStateManager.subscribe(this.authStateChangeHandler.bind(this));
        this.initiateForgotPasswordCB = initiateForgotPasswordCB;
        this.confirmForgotPasswordCB = confirmForgotPasswordCB;
        this.shouldRedirectToGetTokens = shouldRedirectToGetTokens;
        this.transaction = { status: "", data: {} };
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            // Waiting on first updateAuthState event
            const initPromise = new Promise((resolve) => {
                this.initPromiseResolve = resolve;
            });
            if (this.authClient.token.isLoginRedirect()) {
                yield this.authClient.handleLoginRedirect();
            }
            else {
                // Calculate initial auth state and fire change event for listeners
                this.authClient.authStateManager.updateAuthState();
            }
            yield initPromise;
        });
    }
    isFirstLogin() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error("Not implemented");
        });
    }
    isUserDeactivated() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error("Not implemented");
        });
    }
    login(user, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const username = (0, utils_1.normalizeUsername)(user);
            try {
                // try to login & save transaction object
                this.transaction = yield this.authClient.signInWithCredentials({ username, password });
                // Check status
                switch (this.transaction.status) {
                    case TRANSACTION_STATUS.success:
                        yield this.getAndSetTokensWithoutPrompt(this.transaction.sessionToken);
                        this.userName = username;
                        break;
                    case TRANSACTION_STATUS.passwordExpired:
                        this._raiseError(errors_1.errors.newPasswordRequired);
                        break;
                    default:
                        this._raiseError(`Transaction status ${this.transaction.status} not handled`);
                }
            }
            catch (err) {
                this._raiseError(err);
            }
        });
    }
    loginWithRedirect(user) {
        return __awaiter(this, void 0, void 0, function* () {
            // Authenticate with redirect
            yield this.authClient.token.getWithRedirect({
                state: JSON.stringify({
                    issuer: this.oktaAuthOptions.issuer,
                    clientId: this.oktaAuthOptions.clientId,
                    storage: this.oktaAuthOptions.tokenManager.storage,
                    scopes: this.oktaAuthOptions.scopes.join(" "),
                    flow: "redirect",
                    requireUserSession: "true"
                }),
                loginHint: user
            });
        });
    }
    loginWithPopup(user) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Authenticate with popup
                const tokenResponse = yield this.authClient.token.getWithPopup({
                    state: JSON.stringify({
                        issuer: this.oktaAuthOptions.issuer,
                        clientId: this.oktaAuthOptions.clientId,
                        storage: this.oktaAuthOptions.tokenManager.storage,
                        scopes: this.oktaAuthOptions.scopes.join(" "),
                        requireUserSession: "true"
                    }),
                    loginHint: user
                });
                this.authClient.tokenManager.setTokens(tokenResponse.tokens);
                this.authClient.authStateManager.updateAuthState();
            }
            catch (err) {
                this._raiseError(err);
            }
        });
    }
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            this.userInfo = null;
            // Sign out using the default options
            yield this.authClient.signOut();
            try {
                yield this.authClient.session.get();
                this.userName = undefined;
            }
            catch (err) {
                this._raiseError(err);
            }
        });
    }
    isUserManaged(user) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const webFingerResponse = yield this.authClient.webfinger({
                resource: `acct:${user}`
            });
            this.userManaged = !((_c = (_b = (_a = webFingerResponse === null || webFingerResponse === void 0 ? void 0 : webFingerResponse["links"]) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.properties) === null || _c === void 0 ? void 0 : _c["okta:idp:id"]);
            return this.userManaged;
        });
    }
    getUserAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const authState = this.authClient.authStateManager.getAuthState();
            if (authState.accessToken) {
                return authState.accessToken.accessToken;
            }
            this._raiseError(errors_1.errors.userNotLoggedIn);
        });
    }
    getUserIdToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const authState = this.authClient.authStateManager.getAuthState();
            if (authState.idToken) {
                return authState.idToken.idToken;
            }
            this._raiseError(errors_1.errors.userNotLoggedIn);
        });
    }
    getUserName() {
        var _a;
        return (_a = this.userInfo) === null || _a === void 0 ? void 0 : _a.email;
    }
    initiateForgotPassword(user) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.initiateForgotPasswordCB(user);
            }
            catch (err) {
                this._raiseError(err);
            }
        });
    }
    confirmForgotPassword(user, verificationCode, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.confirmForgotPasswordCB(user, verificationCode.trim(), newPassword);
                yield this.login(user, newPassword);
            }
            catch (err) {
                this._raiseError(err);
            }
        });
    }
    changePassword(user, oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            // @ts-ignore
            yield this.transaction.changePassword({ oldPassword, newPassword });
            yield this.login(user, newPassword);
        });
    }
    getAndSetTokensWithoutPrompt(sessionToken) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Get tokens
                const tokenResponse = yield this.authClient.token.getWithoutPrompt({
                    sessionToken: sessionToken,
                    scopes: ["openid", "email", "profile"]
                });
                this.authClient.tokenManager.setTokens(tokenResponse.tokens);
                this.authClient.authStateManager.updateAuthState();
            }
            catch (err) {
                this._raiseError(err);
            }
        });
    }
    /**
     * When updateAuthState is called, a new authState object is produced.
     * `transformAuthState` can modify this object before it is stored and emitted.
     * By default, `updateAuthState` will set `isAuthenticated` to true if unexpired tokens are available from tokenManager.
     * We use it to also require a valid Okta SSO.
     *
     * @param _authClient AuthClient (unused)
     * @param authState {AuthState} authState
     * @private
     */
    transformAuthState(_authClient, authState) {
        return __awaiter(this, void 0, void 0, function* () {
            let responseAuthState = authState;
            // Require the user to have not only valid tokens, but a valid Okta SSO session as well
            if (responseAuthState.accessToken && responseAuthState.idToken) {
                if (!this.userInfo) {
                    this.userInfo = yield _authClient.token.getUserInfo();
                }
                responseAuthState = Object.assign(Object.assign({}, responseAuthState), { isAuthenticated: responseAuthState.isAuthenticated && !!this.userInfo });
            }
            return responseAuthState;
        });
    }
    /**
     * Handler for authState change event. Logic based on authState is done here.
     * @param authState
     * @private
     */
    authStateChangeHandler(authState) {
        if (!authState.isAuthenticated) {
            // If not authenticated, reset values related to user session
            this.userInfo = null;
            if (!authState.isPending && this.shouldRedirectToGetTokens) {
                // If an Okta SSO exists, the redirect will return a code which can be exchanged for tokens
                // If a session does not exist, it will return with "error=login_required"
                this.authClient.token.getWithRedirect({
                    state: JSON.stringify({
                        issuer: this.oktaAuthOptions.issuer,
                        clientId: this.oktaAuthOptions.clientId,
                        storage: this.oktaAuthOptions.tokenManager.storage,
                        scopes: this.oktaAuthOptions.scopes.join(" "),
                        flow: "redirect",
                        requireUserSession: "true"
                    }),
                    prompt: "none"
                }).then(() => {
                    // not resolving promise since we're redirecting
                }).catch(() => {
                    this.resolveInit();
                });
            }
            else {
                this.resolveInit();
            }
        }
        else {
            this.resolveInit();
        }
    }
    // Resolve init Promise
    resolveInit() {
        if (this.initPromiseResolve) {
            this.initPromiseResolve();
            this.initPromiseResolve = undefined;
        }
    }
    _raiseError(err) {
        throw err;
    }
    subscribe(callback) {
        this.authClient.authStateManager.subscribe((state) => callback({
            isPending: state.isPending,
            isAuthenticated: state.isAuthenticated
        }));
    }
    getAuthServiceType() {
        return authService_1.AuthServiceType.OKTA;
    }
}
exports.SskyOktaService = SskyOktaService;
//# sourceMappingURL=SskyOktaService.js.map